import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { Subject, finalize, of, switchMap, catchError } from 'rxjs';

import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { SelectDateComponent } from '@app/shared/select-date/select-date.component';
import { AlertService } from '@app/shared/alert.service';
import { SpinnerService } from '@app/shared/spinner.service';
import { PrintService } from '@app/shared/print.service';
import { businessDayOf, dayNoonIso, pickedDateStr } from '@app/shared/time';
import { serverMessage } from '@app/sales/rental-billing/rental-billing.service';
import { rentalSubtitle, rentalTitle } from '@app/sales/rental-billing/rental-billing.labels';
import { partnerName } from '@app/sales/rental-contract/rental-patient';
import { RenewalLine, RenewalList, RenewalPatient, RentalPrescriptionRenewalService, blobErrorText, coverageLabel } from './rental-prescription-renewal.service';

// Facturation › Fonctions Location › Renouvellement d'ordonnance : patients dont une ordonnance manque ou expire, une lettre chacun, un seul PDF (documentation.html#front.rental.renouvellement-ordonnance).
@Component({
  selector: 'app-rental-prescription-renewal',
  standalone: true,
  templateUrl: './rental-prescription-renewal.component.html',
  styleUrls: ['./rental-prescription-renewal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MenuCommonComponent, SelectDateComponent, RouterLink, MatButtonModule, MatCheckboxModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
})
export class RentalPrescriptionRenewalComponent implements OnInit {
  private service = inject(RentalPrescriptionRenewalService);
  private printService = inject(PrintService);
  private spin = inject(SpinnerService);
  private alertService = inject(AlertService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  list: RenewalList | null = null;
  loading = false;
  printing = false;
  // Jour sous le sélecteur ; null tant que le serveur n'a pas dit le sien (aujourd'hui + c_values 34).
  until: Date | undefined;
  private untilIso: string | null = null;
  selected = new Set<number>();
  private load$ = new Subject<string | null>();

  readonly name = partnerName;
  readonly coverage = coverageLabel;

  ngOnInit(): void {
    this.load$
      .pipe(
        switchMap((until) => {
          this.loading = true;
          return this.service.list$(until).pipe(
            catchError((e) => {
              this.alertService.error('ERREUR Serveur : ' + serverMessage(e));
              return of(null);
            }),
            finalize(() => (this.loading = false)),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((list) => {
        this.list = list;
        if (list) {
          this.untilIso = list.until;
          this.until = this.asLocalDate(businessDayOf(list.until));
          const kept = new Set(list.patients.map((p) => p.id));
          this.selected = new Set([...this.selected].filter((id) => kept.has(id)));
        }
        this.cdr.markForCheck();
      });
    this.load$.next(null);
  }

  get patients(): RenewalPatient[] {
    return this.list?.patients ?? [];
  }

  reload(): void {
    this.load$.next(this.untilIso);
  }

  untilChange(date: Date | string | null): void {
    const day = pickedDateStr(date as any);
    if (!day) return;
    this.untilIso = dayNoonIso(day);
    this.load$.next(this.untilIso);
  }

  isSelected(id: number): boolean {
    return this.selected.has(id);
  }

  toggle(id: number): void {
    if (this.selected.has(id)) this.selected.delete(id);
    else this.selected.add(id);
  }

  selectAll(): void {
    this.selected = new Set(this.patients.map((p) => p.id));
  }

  deselectAll(): void {
    this.selected.clear();
  }

  isAllSelected(): boolean {
    return this.patients.length > 0 && this.patients.every((p) => this.selected.has(p.id));
  }

  isPartiallySelected(): boolean {
    return this.selected.size > 0 && !this.isAllSelected();
  }

  toggleSelectAll(): void {
    if (this.isAllSelected()) this.deselectAll();
    else this.selectAll();
  }

  lineCount(): number {
    return this.patients.reduce((n, p) => n + p.lines.length, 0);
  }

  dossiers(p: RenewalPatient): number[] {
    return [...new Set(p.lines.map((l) => l.order_id))];
  }

  isExpired(l: RenewalLine): boolean {
    return !l.covered_until;
  }

  // Une rangée par matériel, alignée sur les colonnes voisines : l'article passe en infobulle sous l'intitulé du contrat.
  title(l: RenewalLine): string {
    return rentalTitle(l) ?? '';
  }

  article(l: RenewalLine): string {
    return rentalSubtitle(l) ?? '';
  }

  // Lettres dans l'ordre de l'écran (par nom) : le paquet sort prêt à mettre sous pli.
  print(): void {
    const ids = this.patients.filter((p) => this.selected.has(p.id)).map((p) => p.id);
    if (!ids.length || this.printing) return;
    this.printing = true;
    const n = ids.length;
    this.service
      .pdf$(ids, this.untilIso)
      .pipe(this.spin.block(`Préparation de ${n} demande${n > 1 ? 's' : ''} de renouvellement…`), finalize(() => (this.printing = false)))
      .subscribe({
        next: (blob) => this.printService.printPdf(blob),
        error: (e) => blobErrorText(e).then((t) => this.alertService.error(t || 'ERREUR lors de la génération des demandes de renouvellement')),
      });
  }

  // « AAAA-MM-JJ » lu en jour LOCAL pour le sélecteur (new Date('AAAA-MM-JJ') vaudrait minuit UTC, la veille à l'ouest).
  private asLocalDate(day: string | null): Date | undefined {
    const m = day ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(day) : null;
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : undefined;
  }
}
